// @flow
import * as React from 'react';
import {Component} from 'react';
import PropTypes from 'prop-types';
import lowPriorityWarning from '@homeaway/low-priority-warning';

import ExperimentContext from './ExperimentContext';
import ExperimentChildrenContext, {
    NoExperimentChildrenContextProviderError,
    throwIfNoExperimentChildrenContextProvider
} from './ExperimentChildrenContext';
import {ON, OFF} from '../../constants';
import {
    deprecate,
    evaluationIsVariant,
    logExperiment,
    parseBucketConfig
} from '../../utils';

import type {AbacusEvaluationData, ExperimentVariant} from '../../types';
import type {ExperimentContextValue} from './ExperimentContext';

type ExperimentOnOffProps = {
    children?: React.Node
};

type ExperimentVariantProps = ExperimentOnOffProps & {
    variant: ExperimentVariant
};

/**
 * Variant Component.
 *
 * Renders if nearest parent Experiment component is bucketed into the specified
 * variant.
 */
export const Variant = ({children, variant}: ExperimentVariantProps) => (
    <ExperimentChildrenContext.Consumer>
        {(evaluationData) =>
            throwIfNoExperimentChildrenContextProvider(evaluationData) &&
            evaluationIsVariant(evaluationData, variant)
                ? children
                : null
        }
    </ExperimentChildrenContext.Consumer>
);
Variant.propTypes = {
    /** Nodes to render if bucketed into the requested experiment and variant */
    children: PropTypes.node,
    /** Sets the variant in which the
     * component's children should be rendered. */
    variant: PropTypes.oneOfType([
        PropTypes.oneOf(['on', 'off']),
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.number)
    ])
};

const OnOffPropTypes = {
    /** Nodes to render if bucketed into the requested experiment and variant */
    children: PropTypes.node
};

/**
 * On Component.
 *
 * Renders if nearest parent Experiment component is active and bucketed into
 * any variant other than control. Implemented as a partially-applied Variant
 * function to avoid an extra <Variant> component in the tree.
 */
export const On = ({children}: ExperimentOnOffProps) =>
    Variant({children, variant: 'on'}); // eslint-disable-line new-cap
On.propTypes = OnOffPropTypes;

/**
 * Off Component.
 *
 * Renders if nearest parent Experiment component is bucketed into control or is
 * inactive. Implemented as a partially-applied Variant
 * function to avoid an extra <Variant> component in the tree.
 */
export const Off = ({children}: ExperimentOnOffProps) =>
    Variant({children, variant: 'off'}); // eslint-disable-line new-cap
Off.propTypes = OnOffPropTypes;

/**
 * Args exposed via renderProp function when using Experiment components with
 * function children:
 *  <Experiment name="MY_TEST" variant={1}>
 *      {({bucketConfig}) => <p>{bucketConfig}</p>}
 *  </Experiment>
 */
type ExperimentRenderProps = {
    On: typeof On,
    Off: typeof Off,
    Variant: typeof Variant,
    bucketConfig: mixed,
    logExperiment: Function
};

type ExperimentChildren = React.Node | ((ExperimentRenderProps) => React.Node);

const isNullOrUndefined = (val: mixed): %checks =>
    val === null || typeof val === 'undefined';

/**
 * Default empty evaluationData with no bucketConfig
 */
const EVALUATION_DATA_NO_BUCKET_CONFIG = {bucket: {}};

/**
 * Extracts `bucketConfig` from evaluationData and speculatively parses JSON contents.
 * @param {AbacusEvaluationData} evaluationData
 * @returns {mixed} JSON-parsed data or raw bucketConfig string (if unparsable)
 */
const getBucketConfig = ({
    bucket: {bucketConfig}
}: AbacusEvaluationData = EVALUATION_DATA_NO_BUCKET_CONFIG) =>
    parseBucketConfig(bucketConfig);

type ExperimentProps = {
    children?: ExperimentChildren,
    deferLogging: boolean,
    name: string,
    variant?: ExperimentVariant
};

const ExperimentPropTypes = {
    /** Nodes to render if bucketed into the requested experiment and variant,
     * or function. If a function, will be invoked with On, Off, and Variant
     * components, a logExperiment function and the experiment's
     * bucketConfig, if present. */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /** Flag to defer logging an exposure on `componentDidMount`. To be used
     * when test exposure might be gated by a user action or when an element
     * scrolls into view. */
    deferLogging: PropTypes.bool.isRequired,
    /** The name of the experiment. */
    name: PropTypes.string.isRequired,
    /** If not using On, Off or Variant child components, sets the variant in
     * which the component's children should be rendered. */
    variant: PropTypes.oneOfType([
        PropTypes.oneOf(['on', 'off']),
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.number)
    ])
};

class Experiment extends Component<ExperimentProps> {
    static contextType = ExperimentContext;
    static propTypes = ExperimentPropTypes;
    static defaultProps = {
        deferLogging: false
    };
    static ON = ON;
    static OFF = OFF;

    componentDidMount() {
        const {deferLogging} = this.props;
        const evaluationData = this.getEvaluationData();
        if (!deferLogging && evaluationData) {
            logExperiment(evaluationData);
        }
    }

    componentDidCatch(error: Error) {
        if (
            !isNullOrUndefined(this.props.variant) &&
            error instanceof NoExperimentChildrenContextProviderError
        ) {
            // istanbul ignore next
            const variant = Array.isArray(this.props.variant)
                ? `[${this.props.variant.join(',')}]`
                : this.props.variant;

            throw new Error(
                `Used On, Off, or Variant components with <Experiment name="${
                    this.props.name
                }" variant={${variant}}/>. The variant prop overrides child components and not all branches may be reachable. For best results, remove the variant prop if you want to use the On, Off, or Variant components.`
            );
        }

        throw error;
    }

    // Required to ensure `this.context` receives the correct type.
    // c.f. https://github.com/facebook/flow/issues/7166
    context: ExperimentContextValue;

    deferredExperimentLogger = () => {
        const evaluationData = this.getEvaluationData();
        if (this.props.deferLogging && evaluationData) {
            return logExperiment(evaluationData);
        }
        return lowPriorityWarning(
            !this.props.deferLogging,
            `Warning: called yielded logExperiment function for experiment ${
                this.props.name
            }, but experiment was already logged during componentDidMount. To defer logging, add the "deferLogging" prop to your <Experiment> component.`
        );
    };

    On = deprecate(On, this.getYieldedComponentDeprecationMessage());
    Off = deprecate(Off, this.getYieldedComponentDeprecationMessage());
    Variant = deprecate(Variant, this.getYieldedComponentDeprecationMessage());

    getEvaluationData() {
        const abacus = this.context;
        const {name} = this.props;
        return abacus[name];
    }

    getYieldedComponentDeprecationMessage() {
        return `The On, Off, and Variant components yielded by <Experiment/>'s render prop child function are deprecated in favor of the new standalone On, Off and Variant components exported by react-experiment. To learn how to handle this deprecation, see https://Go/hrex-001`;
    }

    renderChildren() {
        const {children} = this.props;
        const evaluationData = this.getEvaluationData();

        return typeof children === 'function'
            ? children({
                  On: this.On,
                  Off: this.Off,
                  Variant: this.Variant,
                  bucketConfig: getBucketConfig(evaluationData),
                  logExperiment: this.deferredExperimentLogger
              })
            : children;
    }

    render() {
        const {variant} = this.props;
        const evaluationData = this.getEvaluationData();

        if (isNullOrUndefined(variant)) {
            return (
                <ExperimentChildrenContext.Provider value={evaluationData}>
                    {this.renderChildren()}
                </ExperimentChildrenContext.Provider>
            );
        }

        return evaluationIsVariant(evaluationData, variant)
            ? this.renderChildren() || null
            : null;
    }
}

export default Experiment;
